/**
 * Notes Service
 *
 * Offline-first service for managing notes, mirroring `countdownService`.
 * All writes follow the pattern: validate → enforce storage/count limits →
 * persist to IndexedDB (`db.notes.put`) → enqueue a sync operation for server
 * propagation when connectivity is available.
 *
 * Key behaviors:
 * - Every operation requires an authenticated (non-empty) `userId`.
 * - Ownership is enforced: `existing.userId === userId` for reads/writes.
 * - Title is trimmed before length validation (1-200 chars).
 * - Body is 0-50000 chars; up to 50 links on creation (100 hard cap per note).
 * - Up to 20 tags; duplicate tags/links are deduped.
 * - Deletes are soft-deletes (sets `deleted = true`).
 *
 * @module notesService
 * @see Requirements 2, 3, 4, 10, 12
 */

import { v4 as uuidv4 } from 'uuid';
import type { Note, NoteLink } from '../types/notes';
import type { ExtendedSyncQueueItem } from '../types/modules';
import type { SyncQueueItem } from '../types';
import { SyncStatus } from '../types/enums';
import {
  db,
  MAX_NOTE_COUNT,
  enqueueSyncItem,
  getStorageEstimate,
} from './localDb';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Minimum title length in characters (after trimming). */
export const TITLE_MIN = 1;
/** Maximum title length in characters. */
export const TITLE_MAX = 200;
/** Maximum body length in characters. */
export const BODY_MAX = 50000;
/** Soft cap on the number of links allowed on note creation (R2.1). */
export const LINKS_SOFT_MAX = 50;
/** Hard cap on the number of links per note (R5.1, R5.3). */
export const LINKS_HARD_MAX = 100;
/** Maximum number of tags per note. */
export const TAGS_MAX = 20;
/** Minimum link label length in characters (must have a non-whitespace char). */
export const LINK_LABEL_MIN = 1;
/** Maximum link label length in characters. */
export const LINK_LABEL_MAX = 200;
/** Maximum link URL length in characters. */
export const LINK_URL_MAX = 2048;

/** Maximum local storage size in bytes (mirrors localDb; 1 GB). */
const MAX_STORAGE_BYTES = 1_073_741_824;

// ─── Types ───────────────────────────────────────────────────────────────────

/** Input for creating a new note. */
export interface CreateNoteInput {
  title: string;
  body?: string;
  links?: Array<Omit<NoteLink, 'id'>>;
  tags?: string[];
  pinned?: boolean;
}

/** Input for updating an existing note. */
export type UpdateNoteInput = Partial<Pick<Note, 'title' | 'body' | 'tags' | 'pinned'>>;

/** Query parameters for filtering notes. */
export interface NoteQuery {
  /** Trimmed, case-insensitive substring over title/body/tag names. */
  searchText?: string;
  /** Tag IDs to filter by, using AND semantics. */
  tagIds?: string[];
}

// ─── Sync helper ──────────────────────────────────────────────────────────────

/**
 * Enqueue a sync item for the notes module.
 *
 * Delegates to the shared `enqueueSyncItem`, which enforces the sync-queue
 * capacity limit and throws when full (surfaced as a sync-queue-full error).
 */
async function enqueueNoteChange(
  entityId: string,
  action: 'create' | 'update' | 'delete',
  payload: Record<string, unknown>
): Promise<void> {
  const item: Omit<ExtendedSyncQueueItem, 'id'> = {
    module: 'notes',
    entityId,
    action,
    timestamp: new Date().toISOString(),
    payload,
    retryCount: 0,
  };
  await enqueueSyncItem(item as unknown as Omit<SyncQueueItem, 'id'>);
}

// ─── Validation Helpers ─────────────────────────────────────────────────────

/**
 * Ensure an authenticated userId is present.
 * @throws Error if userId is empty (R12.1, R12.2).
 */
function requireUserId(userId: string): void {
  if (!userId || userId.trim().length === 0) {
    throw new Error('Authentication required: a valid userId is required.');
  }
}

/**
 * Validate a note title after trimming (1-200 chars).
 * @returns The trimmed title.
 * @throws Error if the title is invalid.
 */
function validateTitle(title: string): string {
  const trimmed = (title ?? '').trim();
  if (trimmed.length < TITLE_MIN) {
    throw new Error(`Note title must be at least ${TITLE_MIN} character(s).`);
  }
  if (trimmed.length > TITLE_MAX) {
    throw new Error(`Note title must not exceed ${TITLE_MAX} characters.`);
  }
  return trimmed;
}

/**
 * Validate a note body (0-50000 chars).
 * @throws Error if the body is too long.
 */
function validateBody(body: string): void {
  if (body.length > BODY_MAX) {
    throw new Error(`Note body must not exceed ${BODY_MAX} characters.`);
  }
}

/**
 * Validate a single link's label and URL.
 * Label: 1-200 chars with at least one non-whitespace character.
 * URL: 1-2048 chars, http or https scheme only.
 * @throws Error identifying the offending field.
 */
function validateLink(link: Omit<NoteLink, 'id'>): void {
  const label = link.label ?? '';
  if (label.trim().length < LINK_LABEL_MIN) {
    throw new Error(
      `Link label must contain at least ${LINK_LABEL_MIN} non-whitespace character(s).`
    );
  }
  if (label.length > LINK_LABEL_MAX) {
    throw new Error(`Link label must not exceed ${LINK_LABEL_MAX} characters.`);
  }

  const url = link.url ?? '';
  if (url.length < 1) {
    throw new Error('Link URL must not be empty.');
  }
  if (url.length > LINK_URL_MAX) {
    throw new Error(`Link URL must not exceed ${LINK_URL_MAX} characters.`);
  }
  if (!isHttpUrl(url)) {
    throw new Error('Link URL must use the http or https scheme.');
  }
}

/**
 * Check whether a URL string uses the http or https scheme.
 */
function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validate a set of tags (max 20).
 * @throws Error if too many tags are provided.
 */
function validateTags(tags: string[]): void {
  if (tags.length > TAGS_MAX) {
    throw new Error(`A note may have at most ${TAGS_MAX} tags.`);
  }
}

/**
 * Deduplicate tag IDs, preserving first-seen order (R2.7, R8.4).
 */
function dedupeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const tag of tags) {
    if (!seen.has(tag)) {
      seen.add(tag);
      result.push(tag);
    }
  }
  return result;
}

/**
 * Deduplicate links by (label, url) pair, preserving first-seen order,
 * and assign a fresh id to each unique link (R2.7).
 */
function dedupeLinks(links: Array<Omit<NoteLink, 'id'>>): NoteLink[] {
  const seen = new Set<string>();
  const result: NoteLink[] = [];
  for (const link of links) {
    const key = `${link.label}\u0000${link.url}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ id: uuidv4(), label: link.label, url: link.url });
    }
  }
  return result;
}

/**
 * Enforce local storage and note-count limits before inserting a new note.
 * Mirrors `putEntry` in localDb (R10.5, R10.6).
 * @throws Error if a limit would be exceeded.
 */
async function enforceStorageLimits(): Promise<void> {
  const count = await db.notes.count();
  if (count >= MAX_NOTE_COUNT) {
    throw new Error(
      `Offline storage limit reached: maximum ${MAX_NOTE_COUNT} notes allowed.`
    );
  }
  const storageBytes = await getStorageEstimate();
  if (storageBytes >= MAX_STORAGE_BYTES) {
    throw new Error(
      'Offline storage limit reached: maximum 1GB of local storage allowed.'
    );
  }
}

// ─── CRUD Service Functions ───────────────────────────────────────────────────

/**
 * Create a new note.
 *
 * Validates input, enforces storage/count limits, persists to IndexedDB
 * (persist first), then enqueues a sync operation.
 *
 * @param input - Note creation parameters.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The created Note.
 * @throws Error if validation fails or a storage limit is reached.
 */
export async function createNote(
  input: CreateNoteInput,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  // Validate
  const title = validateTitle(input.title);
  const body = input.body ?? '';
  validateBody(body);

  const rawLinks = input.links ?? [];
  if (rawLinks.length > LINKS_SOFT_MAX) {
    throw new Error(`A note may be created with at most ${LINKS_SOFT_MAX} links.`);
  }
  for (const link of rawLinks) {
    validateLink(link);
  }
  const links = dedupeLinks(rawLinks);

  const rawTags = input.tags ?? [];
  validateTags(rawTags);
  const tags = dedupeTags(rawTags);

  // Enforce storage/count limits before insert
  await enforceStorageLimits();

  const now = new Date().toISOString();
  const note: Note = {
    noteId: uuidv4(),
    userId,
    title,
    body,
    links,
    tags,
    pinned: input.pinned ?? false,
    deleted: false,
    syncStatus: SyncStatus.Pending,
    createdAt: now,
    updatedAt: now,
  };

  // Persist first
  await db.notes.put(note);

  // Then enqueue sync
  await enqueueNoteChange(note.noteId, 'create', note as unknown as Record<string, unknown>);

  return note;
}

/**
 * Update an existing note.
 *
 * Validates any changed fields, enforces ownership, persists to IndexedDB
 * (persist first), then enqueues a sync operation.
 *
 * @param noteId - The ID of the note to update.
 * @param updates - Partial note fields to update.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note is not found, not owned, or validation fails.
 */
export async function updateNote(
  noteId: string,
  updates: UpdateNoteInput,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  let title = existing.title;
  if (updates.title !== undefined) {
    title = validateTitle(updates.title);
  }

  let body = existing.body;
  if (updates.body !== undefined) {
    validateBody(updates.body);
    body = updates.body;
  }

  let tags = existing.tags;
  if (updates.tags !== undefined) {
    validateTags(updates.tags);
    tags = dedupeTags(updates.tags);
  }

  const pinned = updates.pinned !== undefined ? updates.pinned : existing.pinned;

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    title,
    body,
    tags,
    pinned,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Soft-delete a note.
 *
 * Sets `deleted = true`, persists to IndexedDB (persist first), then enqueues
 * a sync delete operation. Idempotent: deleting an already-deleted note simply
 * re-persists the deleted state.
 *
 * @param noteId - The ID of the note to delete.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @throws Error if the note is not found or not owned.
 */
export async function deleteNote(noteId: string, userId: string): Promise<void> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  const now = new Date().toISOString();
  const deleted: Note = {
    ...existing,
    deleted: true,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(deleted);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'delete', { noteId, userId });
}

/**
 * Get all non-deleted notes for a user.
 *
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns Array of non-deleted Notes, ordered by updatedAt descending.
 */
export async function getNotes(userId: string): Promise<Note[]> {
  requireUserId(userId);

  const notes = await db.notes
    .where('userId')
    .equals(userId)
    .and((n) => !n.deleted)
    .toArray();

  return notes.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Get all notes for a user, INCLUDING done/soft-deleted ones.
 *
 * Mirrors {@link getNotes} but omits the `deleted` filter so notes marked
 * "done" (which reuse the `deleted` flag) remain visible to the UI (rendered
 * struck-through rather than hidden). Scoped to the owning user and ordered by
 * updatedAt descending.
 *
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns Array of all the user's Notes, ordered by updatedAt descending.
 */
export async function getAllNotes(userId: string): Promise<Note[]> {
  requireUserId(userId);

  const notes = await db.notes
    .where('userId')
    .equals(userId)
    .toArray();

  return notes.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Toggle a note's "done" state.
 *
 * Reuses the `deleted` boolean as the done/active flag: flips it so a done note
 * becomes active and vice versa. Enforces ownership and follows the
 * persist-first-then-enqueue pattern (mirrors {@link setPinned}): the note is
 * persisted first, `deleted` is set to the flipped value, `syncStatus` becomes
 * Pending, `updatedAt` is bumped, and exactly one `update` sync operation is
 * enqueued.
 *
 * @param noteId - The ID of the note to toggle.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note is not found or not owned.
 */
export async function toggleDone(noteId: string, userId: string): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    deleted: !existing.deleted,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Permanently delete a note.
 *
 * Unlike {@link deleteNote} (a soft-delete that retains the record), this
 * removes the record from IndexedDB entirely. Enforces ownership, then deletes
 * the stored note and enqueues a `delete` sync operation so the removal
 * propagates to the server when connectivity is available.
 *
 * @param noteId - The ID of the note to permanently delete.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @throws Error if the note is not found or not owned.
 */
export async function hardDeleteNote(noteId: string, userId: string): Promise<void> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  // Permanently remove the record first.
  await db.notes.delete(noteId);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'delete', { noteId });
}

/**
 * Get a single note by ID, scoped to the owning user.
 *
 * @param noteId - The ID of the note to retrieve.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The Note if found and owned by the user, otherwise undefined.
 */
export async function getNote(
  noteId: string,
  userId: string
): Promise<Note | undefined> {
  requireUserId(userId);

  const note = await db.notes.get(noteId);
  if (!note || note.userId !== userId) {
    return undefined;
  }
  return note;
}

// ─── Link Operations ──────────────────────────────────────────────────────────

/**
 * Add a link to a note.
 *
 * Validates the link (label 1-200 with a non-whitespace char, URL 1-2048 using
 * only http/https), enforces the per-note hard cap of {@link LINKS_HARD_MAX}
 * links, and dedupes by (label, url) so an identical link is not stored twice.
 * The new link is assigned a fresh UUID and appended to the end of the note's
 * ordered link set. Follows the persist-first-then-enqueue pattern: persists the
 * updated note to IndexedDB before enqueueing an `update` sync operation, sets
 * `syncStatus` to Pending, and bumps `updatedAt`.
 *
 * @param noteId - The ID of the note to add the link to.
 * @param link - The link (label + url) to add; an id is assigned automatically.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note is not found/owned, the link is invalid, or the
 *   per-note link limit has been reached (R5.1, R5.2, R5.3, R5.9, R12.4).
 */
export async function addLink(
  noteId: string,
  link: Omit<NoteLink, 'id'>,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  // Enforce the per-note hard cap before validating/adding (R5.3).
  if (existing.links.length >= LINKS_HARD_MAX) {
    throw new Error(
      `A note may have at most ${LINKS_HARD_MAX} links; the link limit has been reached.`
    );
  }

  // Validate the candidate link (R5.1, R5.2).
  validateLink(link);

  // Dedupe by (label, url): if an identical link already exists, this is a no-op
  // that does not create a duplicate (R2.7).
  const alreadyPresent = existing.links.some(
    (l) => l.label === link.label && l.url === link.url
  );

  let links = existing.links;
  if (!alreadyPresent) {
    const newLink: NoteLink = { id: uuidv4(), label: link.label, url: link.url };
    links = [...existing.links, newLink];
  }

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    links,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Update an existing link's label and/or URL within a note.
 *
 * Applies the given partial patch to the target link, validates the merged
 * result (label 1-200 with a non-whitespace char, URL 1-2048 http/https only),
 * and dedupes by (label, url) so the edit does not produce a duplicate entry.
 * Follows the persist-first-then-enqueue pattern, sets `syncStatus` to Pending,
 * and bumps `updatedAt`.
 *
 * @param noteId - The ID of the note containing the link.
 * @param linkId - The ID of the link to update.
 * @param patch - Partial link fields (label and/or url) to apply.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note or link is not found/owned or validation fails
 *   (R5.4, R5.2, R5.9, R12.4).
 */
export async function updateLink(
  noteId: string,
  linkId: string,
  patch: Partial<Omit<NoteLink, 'id'>>,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  const index = existing.links.findIndex((l) => l.id === linkId);
  if (index === -1) {
    throw new Error('Link not found.');
  }

  const current = existing.links[index];
  if (!current) {
    throw new Error('Link not found.');
  }
  const merged: NoteLink = {
    id: current.id,
    label: patch.label !== undefined ? patch.label : current.label,
    url: patch.url !== undefined ? patch.url : current.url,
  };

  // Validate the merged result (R5.4, R5.2).
  validateLink({ label: merged.label, url: merged.url });

  // Dedupe by (label, url): reject collapsing this link onto another existing
  // link with the same (label, url) so we never store a duplicate (R2.7).
  const collidesWithOther = existing.links.some(
    (l) => l.id !== linkId && l.label === merged.label && l.url === merged.url
  );
  if (collidesWithOther) {
    throw new Error(
      'A link with the same label and URL already exists on this note.'
    );
  }

  const links = existing.links.map((l, i) => (i === index ? merged : l));

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    links,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Remove a link from a note.
 *
 * Removes the target link from the note's ordered link set, preserving the
 * relative order of the remaining links. Follows the persist-first-then-enqueue
 * pattern, sets `syncStatus` to Pending, and bumps `updatedAt`.
 *
 * @param noteId - The ID of the note containing the link.
 * @param linkId - The ID of the link to remove.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note or link is not found/owned (R5.5, R5.9, R12.4).
 */
export async function removeLink(
  noteId: string,
  linkId: string,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  const index = existing.links.findIndex((l) => l.id === linkId);
  if (index === -1) {
    throw new Error('Link not found.');
  }

  const links = existing.links.filter((l) => l.id !== linkId);

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    links,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

// ─── Tag Operations ───────────────────────────────────────────────────────────

/**
 * Add a tag to a note.
 *
 * Enforces ownership, the per-note tag cap of {@link TAGS_MAX}, and dedupe. If
 * the tag is already present on the note this is a successful no-op: the note's
 * tags, `updatedAt`, and `syncStatus` are left unchanged and no sync item is
 * enqueued (R8.4). On a real change the tag is appended, the note is persisted
 * first, `syncStatus` is set to Pending, `updatedAt` is bumped, and exactly one
 * `update` sync operation is enqueued (persist-first-then-enqueue).
 *
 * @param noteId - The ID of the note to add the tag to.
 * @param tagId - The tag ID to assign.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated (or unchanged, on no-op) Note.
 * @throws Error if the note is not found/owned or the tag limit is reached
 *   (R8.1, R8.3, R8.4, R8.8, R12.4).
 */
export async function addTag(
  noteId: string,
  tagId: string,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  // Dedupe: assigning an already-present tag is a successful no-op that leaves
  // tags, updatedAt, and syncStatus unchanged and enqueues nothing (R8.4).
  if (existing.tags.includes(tagId)) {
    return existing;
  }

  // Enforce the per-note tag cap before adding (R8.3).
  if (existing.tags.length >= TAGS_MAX) {
    throw new Error(
      `A note may have at most ${TAGS_MAX} tags; the tag limit has been reached.`
    );
  }

  const tags = dedupeTags([...existing.tags, tagId]);
  validateTags(tags);

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    tags,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

/**
 * Remove a tag from a note.
 *
 * Enforces ownership and removes the tag reference from the note. Follows the
 * persist-first-then-enqueue pattern: persists the updated note to IndexedDB
 * before enqueueing an `update` sync operation, sets `syncStatus` to Pending,
 * and bumps `updatedAt`.
 *
 * @param noteId - The ID of the note to remove the tag from.
 * @param tagId - The tag ID to remove.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated Note.
 * @throws Error if the note is not found/owned (R8.2, R8.8, R12.4).
 */
export async function removeTag(
  noteId: string,
  tagId: string,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  const tags = existing.tags.filter((t) => t !== tagId);

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    tags,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

// ─── Pin Operations ─────────────────────────────────────────────────────────

/**
 * Set a note's pinned state.
 *
 * Enforces ownership. When the requested `pinned` value matches the note's
 * current state this is a no-op: `pinned`, `updatedAt`, and `syncStatus` are
 * left unchanged and no sync item is enqueued (R9.4). On a real state change the
 * note is persisted first, `pinned` is updated, `syncStatus` is set to Pending,
 * `updatedAt` is bumped, and exactly one `update` sync operation is enqueued
 * (persist-first-then-enqueue, R9.2/R9.3/R9.9). If persistence fails the
 * previous `pinned`/`updatedAt`/`syncStatus` values are retained because the
 * stored note is only mutated by the (failed) put (R9.5).
 *
 * @param noteId - The ID of the note to pin/unpin.
 * @param pinned - The desired pinned state.
 * @param userId - The current user's Cognito sub ID (non-empty).
 * @returns The updated (or unchanged, on no-op) Note.
 * @throws Error if the note is not found/owned (R9.2, R9.3, R9.4, R9.5, R9.9, R12.4).
 */
export async function setPinned(
  noteId: string,
  pinned: boolean,
  userId: string
): Promise<Note> {
  requireUserId(userId);

  const existing = await db.notes.get(noteId);
  if (!existing || existing.userId !== userId) {
    throw new Error('Note not found.');
  }

  // No-op when the pinned state is unchanged: leave pinned, updatedAt, and
  // syncStatus untouched and enqueue nothing (R9.4).
  if (existing.pinned === pinned) {
    return existing;
  }

  const now = new Date().toISOString();
  const updated: Note = {
    ...existing,
    pinned,
    updatedAt: now,
    syncStatus: SyncStatus.Pending,
  };

  // Persist first — if this rejects, the operation aborts before enqueue and the
  // prior stored note is unchanged (R9.5).
  await db.notes.put(updated);

  // Then enqueue sync
  await enqueueNoteChange(noteId, 'update', updated as unknown as Record<string, unknown>);

  return updated;
}

// ─── Pure Query Helpers ───────────────────────────────────────────────────────

/**
 * Order a list of notes for display.
 *
 * Pure and deterministic (no I/O, no side effects). The `deleted` flag is reused
 * as a "done" marker, so done notes are kept (not dropped) but always sorted to
 * the bottom. The ordering, in priority order:
 * 1. Active notes (`deleted === false`) before done notes (`deleted === true`).
 * 2. Within each active/done group, pinned notes before unpinned notes.
 * 3. Then `updatedAt` descending (most recently updated first).
 * 4. Ties broken by case-insensitive title ascending (R6.5, R9.7, R9.8).
 * The input array is not mutated — a new sorted array is returned.
 *
 * @param notes - The notes to order.
 * @returns A new array ordered active-first, pinned-first, updatedAt desc, title asc.
 */
export function sortNotes(notes: Note[]): Note[] {
  return [...notes].sort((a, b) => {
    // Active notes come before done (deleted) notes.
    if (a.deleted !== b.deleted) {
      return a.deleted ? 1 : -1;
    }
    // Within an active/done group, pinned notes come first.
    if (a.pinned !== b.pinned) {
      return a.pinned ? -1 : 1;
    }
    // Then most recently updated first.
    if (a.updatedAt !== b.updatedAt) {
      return b.updatedAt.localeCompare(a.updatedAt);
    }
    // Ties broken by case-insensitive title ascending.
    return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
  });
}

/**
 * Filter a list of notes by search text and tag IDs.
 *
 * Pure and deterministic (no I/O, no side effects). The `deleted` flag is reused
 * as a "done" marker, so done notes are retained here (they remain visible,
 * rendered struck-through) rather than being dropped. The filtering pipeline:
 * 1. Applies the tag filter with AND semantics — a note is kept only if it
 *    carries every tag ID in `query.tagIds` (R8.5). An empty/omitted `tagIds`
 *    imposes no tag constraint (R8.7).
 * 2. Applies the text filter — a note is kept when the trimmed, lower-cased
 *    `query.searchText` is a substring of its title, body, or any assigned tag
 *    reference, using case-insensitive matching (R7.1). An empty or
 *    whitespace-only `searchText` imposes no text constraint (R7.3).
 * Results are ordered by `updatedAt` descending (R7.6).
 *
 * Note on tag-name search (R7.1): this pure helper only has access to the tag
 * references stored on each note (`Note.tags`, which hold tag IDs), so it
 * matches search text against those references here. Resolution of tag IDs to
 * human-readable tag names for search is layered in the store selector
 * (`selectVisibleNotes`), which has access to the loaded Tag entities — mirroring
 * the design's separation of pure helpers (service) from tag resolution (store).
 *
 * @param notes - The notes to filter.
 * @param query - The search text and/or tag IDs to filter by.
 * @returns A new array of matching notes (done ones retained) ordered by updatedAt desc.
 */
export function filterNotes(notes: Note[], query: NoteQuery): Note[] {
  const trimmedSearch = (query.searchText ?? '').trim().toLowerCase();
  const tagIds = query.tagIds ?? [];

  const matched = notes.filter((note) => {
    // Tag filter — AND semantics: the note must carry every selected tag (R8.5).
    if (tagIds.length > 0) {
      const noteTags = new Set(note.tags);
      const carriesAll = tagIds.every((tagId) => noteTags.has(tagId));
      if (!carriesAll) {
        return false;
      }
    }

    // Text filter — case-insensitive substring over title/body/tag references
    // (R7.1). Empty/whitespace-only search imposes no constraint (R7.3).
    if (trimmedSearch.length > 0) {
      const inTitle = note.title.toLowerCase().includes(trimmedSearch);
      const inBody = note.body.toLowerCase().includes(trimmedSearch);
      const inTags = note.tags.some((tag) =>
        tag.toLowerCase().includes(trimmedSearch)
      );
      if (!inTitle && !inBody && !inTags) {
        return false;
      }
    }

    return true;
  });

  // Order by updatedAt descending (R7.6).
  return matched.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
